import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveOffers,
  selectAppliedOffers,
  selectUnlockedOffers,
  setOffers,
} from '../../../../store/slices/OfferSlice';
import useGetBasketOfferHandlerByKind from '../get-offer-handler/useGetBasketOfferHandlerByKind';
import { Offer } from '../../../../services/exports/Interfaces';
import { CompleteBasketItem } from '../../../../providers/BasketProvider';
import toast from 'react-hot-toast';
import useGetCompanyMenusApi from '../../../../hooks/api/companies/useGetCompanyMenusApi';
import useOffersTranslations from '../useOffersTranslations';
import { useTranslation } from 'react-i18next';
import { selectHasCachedOrderPromoCode } from '../../../../store/slices/OrderSlice';
import collect from 'collect.js';
import { SCREENS } from '../../../../services/exports/Constants';

const useWatchUnlockedOffers = (items: CompleteBasketItem[]) => {
  const activeOffers = useSelector(selectActiveOffers);
  const unlockedOffers = useSelector(selectUnlockedOffers);
  const appliedOffers = useSelector(selectAppliedOffers);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { data } = useGetCompanyMenusApi();
  const dispatch = useDispatch();
  const hasPromoCode = useSelector(selectHasCachedOrderPromoCode);
  const getOfferHandler = useGetBasketOfferHandlerByKind();
  const setUnlockedOffers = (offers: Offer[] = []) => dispatch(setOffers({ unlockedOffers: offers }));
  const { getLabelByOffer } = useOffersTranslations();
  const { t } = useTranslation(null, { keyPrefix: 'Features:Offers:Watchers' });
  const handleToast = (offers: Offer[]) => {
    if (window.location.pathname.includes(SCREENS.ORDER_DETAILS)) {
      return;
    }
    const copy = t('unlocked', { count: offers.length, offer: getLabelByOffer(offers[0]) });

    if (offers.length > 1) {
      return toast(copy, { duration: 3000, position: 'bottom-center' });
    }

    const shouldSkipToast =
      !!appliedOffers?.length && offers.every((o) => appliedOffers.some((appliedOffer) => appliedOffer.id === o.id));

    if (shouldSkipToast) {
      return;
    }

    timeoutRef.current && clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      toast(copy, { duration: 3000, position: 'bottom-center' });
    }, 500);
  };
  const unlockedOffersHandler = (): void => {
    if (hasPromoCode) {
      unlockedOffers.length && setUnlockedOffers();
      return;
    }

    const result: Offer[] = [];
    let completeItems = items;

    for (const offer of activeOffers) {
      const offerHandler = getOfferHandler(offer, completeItems);

      if (!offerHandler.isUnlocked) {
        continue;
      }

      result.push(offer);

      if (!offerHandler.apply) {
        continue;
      }

      completeItems = completeItems.map(offerHandler.apply);
    }

    const newOffers = result.filter((offer) => unlockedOffers.every((unlockedOffer) => unlockedOffer.id !== offer.id));

    newOffers.length && handleToast(newOffers);

    if (!result.length && !unlockedOffers.length) {
      return;
    }

    const unlockedOffersById = collect(unlockedOffers).keyBy('id');

    const hasChanges =
      unlockedOffers.length !== result.length ||
      result.some(
        (offer) =>
          !unlockedOffersById.has(offer.id) ||
          JSON.stringify(unlockedOffersById.get(offer.id)) !== JSON.stringify(offer),
      );

    if (!hasChanges) {
      return;
    }

    setUnlockedOffers(result);
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    unlockedOffersHandler();

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [activeOffers, items, data, appliedOffers, hasPromoCode]);
};

export default useWatchUnlockedOffers;
